import {
  capitalize,
  capitilizeAll,
  createState,
  pluralize,
  RunCode,
  RunCodes,
  Test,
  Threat,
  useAuthConfig,
  useRecentEndpointQuery,
} from "@theprelude/core";
import {
  Button,
  Checkbox,
  notifyError,
  notifySuccess,
  Radio,
  Select,
} from "@theprelude/ds";
import { useState } from "react";
import { z } from "zod";
import { DefaultOverlay } from "@/components/default-overlay";
import { Schedule, useUpsertSchedules } from "@/hooks/schedule-mutations";
import { useOverlay } from "@/hooks/use-overlay";
import { frequencies, frequencyWordsToIgnore } from "@/lib/frequency";
import { scheduleQueryOptions } from "@/queries/schedule";

export const scheduleSchema = z.tuple([
  z.literal("schedule"),
  z.object({
    type: z.enum(["test", "threat"]),
    ids: z.array(z.string()).optional(),
    defaultSelectedId: z.string().optional(),
  }),
]);

type ScheduleSchema = z.infer<typeof scheduleSchema>;
const handleSelectorText = (row: Threat | Test) => {
  const id = `(*${row.id.slice(32, 36)})`;
  if (row.name.length > 40) {
    return `${row.name.slice(0, 32)}... ${id}`;
  }
  return `${row.name} ${id}`;
};

const getSelectedRow = (data: Test[] | Threat[], id: string): Schedule => {
  const row = data.find((row) => row.id === id);
  if (!row) {
    throw new Error("Row not found");
  }
  return {
    name: row.name,
    id: row.id,
    isScheduled: false,
  };
};

export const useScheduleCallback = createState<(() => void) | undefined>(
  undefined,
);

const ScheduleOverlay = ({
  ids = [],
  type,
  defaultSelectedId,
}: ScheduleSchema[1]) => {
  const callback = useScheduleCallback();
  const overlay = useOverlay();
  const authConfig = useAuthConfig();
  const scheduleQuery = scheduleQueryOptions(authConfig).useSuspenseQuery();
  const [selectedRunCode, setSelectedRunCode] = useState<RunCode>();
  const [target, setTarget] = useState<"all" | "tags" | "">("");
  const [selectedId, setSelected] = useState<string | undefined>(
    defaultSelectedId,
  );
  const [selectedTags, setSelectedTags] = useState<string[] | null>();

  const handleSelectedTags = (tag: string) => {
    setSelectedTags((tags) => {
      if (!tags) {
        return [tag];
      }
      if (!tags.includes(tag)) {
        return [...tags, tag];
      }

      const updatedTags = tags.filter((t) => t !== tag);
      if (updatedTags.length > 0) {
        return updatedTags;
      }

      return null;
    });
  };

  const upsertSchedules = useUpsertSchedules();
  const endpointsQuery = useRecentEndpointQuery();
  const endpoints = endpointsQuery.data ?? [];
  const endpointTags = [
    ...new Set(endpoints.flatMap((endpoint) => endpoint.tags)),
  ];

  function getSelectedTagCount() {
    let tagSelectedCount = 0;
    selectedTags?.forEach((tag) => {
      const tagCount = endpoints.filter((endpoint) =>
        endpoint.tags.includes(tag),
      ).length;

      tagSelectedCount += tagCount;
    });

    return tagSelectedCount;
  }

  const data =
    (type === "test"
      ? scheduleQuery.data.tests.data
      : scheduleQuery.data.threats.data) ?? [];

  const rows =
    (type === "test"
      ? scheduleQuery.data.tests.rows
      : scheduleQuery.data.threats.rows) ?? [];

  const selectedRows = selectedId
    ? [getSelectedRow(data, selectedId)]
    : rows.filter((row) => ids.includes(row.rowId));

  const upsert = () => {
    if (selectedRows.length === 0) {
      notifyError({
        message: "Please fill out all fields to create the schedule",
      });
      return;
    }

    const rows: Schedule[] = selectedRows.map((row) => {
      if (
        row.isScheduled &&
        row.target !== undefined &&
        row.runCode !== undefined
      ) {
        return {
          id: row.id,
          name: row.name,
          isScheduled: true,
          target: row.target,
          runCode: row.runCode,
        };
      }

      return {
        name: row.name,
        id: row.id,
        isScheduled: false,
      };
    });

    upsertSchedules.mutate(
      {
        rows,
        type,
        tags: selectedTags,
        runCode: selectedRunCode,
      },
      {
        onSuccess: async () => {
          notifySuccess({
            message: `Successfully updated ${selectedRows.length} ${pluralize(
              rows.length,
              "schedule",
            )}`,
          });

          void overlay.close();

          // This is a workaround to refresh the table after the schedule is updated
          if (callback.value) {
            callback.value();
            callback.setValue(undefined);
          }
        },
        onError: async (error) => {
          notifyError({ error, message: "Failed to update schedules" });
        },
      },
    );
  };

  const unscheduledRows = selectedRows.filter((row) => !row.isScheduled);
  const scheduledRows = selectedRows.filter((row) => row.isScheduled);

  const showTags = target === "tags";
  const isEdit = scheduledRows.length > 0;
  const areRecurringTests = false;
  const hasUnscheduledTest = unscheduledRows.length > 0;

  const nonRowsSelected = ids.length === 0;
  const selectedText =
    unscheduledRows.length === 1 && !isEdit
      ? unscheduledRows[0]?.name
      : `${ids.length} schedules selected`;

  const handleTitleText = () => {
    if (isEdit) {
      return `Edit selected ${pluralize(scheduledRows.length, type)}`;
    }

    return `Schedule ${type}s`;
  };

  return (
    <DefaultOverlay
      title={handleTitleText()}
      onClose={() => {
        callback.setValue(undefined);
      }}
    >
      <form onSubmit={() => upsert()}>
        <div className="p-4">
          <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-2">
              <h5 className="m-0 mb-1 border-stroke-line border-b pb-2 font-normal text-secondary-foreground text-sm leading-5">
                {capitalize(type)}s
              </h5>

              {nonRowsSelected ? (
                <Select
                  value={selectedId}
                  onChange={(e) => setSelected(e.target.value)}
                  defaultValue={`Select a ${type}`}
                >
                  <option disabled>Select a {type}</option>
                  {data
                    .filter((row) => !row.tombstoned)
                    .map((row) => (
                      <option title={row.name} key={row.id} value={row.id}>
                        {handleSelectorText(row)}
                      </option>
                    ))}
                </Select>
              ) : (
                <Select readOnly>
                  <option>{selectedText}</option>
                </Select>
              )}
            </section>
            <section className="flex flex-col gap-2">
              <h5 className="m-0 mb-1 border-stroke-line border-b pb-2 font-normal text-secondary-foreground text-sm leading-5">
                Frequency
              </h5>
              {isEdit && !hasUnscheduledTest && (
                <div className="flex items-center gap-2 leading-5">
                  <Radio
                    id={"frequency-no-change"}
                    checked={selectedRunCode === undefined}
                    onChange={() => setSelectedRunCode(undefined)}
                  />
                  <label htmlFor="frequency-no-change">No change</label>
                </div>
              )}
              {Object.values(RunCodes)
                .filter((runCode) => runCode !== -1 && runCode !== 5)
                .map((runCode) => (
                  <div
                    className="flex items-center gap-2 leading-5"
                    key={runCode}
                  >
                    <Radio
                      id={runCode.toString()}
                      checked={selectedRunCode === runCode}
                      onChange={() => setSelectedRunCode(runCode)}
                    />
                    <label htmlFor={runCode.toString()}>
                      {capitilizeAll(
                        frequencies.get(runCode) ?? "Unknown",
                        frequencyWordsToIgnore,
                      )}
                    </label>
                  </div>
                ))}
              <RunCodeBlurb runCode={selectedRunCode} />
            </section>
            <section className="flex flex-col gap-2">
              <h5 className="m-0 mb-1 border-stroke-line border-b pb-2 font-normal text-secondary-foreground text-sm leading-5">
                Target
              </h5>
              <>
                {(areRecurringTests || isEdit) && !hasUnscheduledTest && (
                  <div className="flex items-center gap-2 leading-5">
                    <Radio
                      id="target-no-change"
                      checked={target === ""}
                      onChange={() => {
                        setTarget("");
                        setSelectedTags(null);
                      }}
                    />
                    <label htmlFor="target-no-change">No change</label>
                  </div>
                )}
                <div className="flex items-center gap-2 leading-5">
                  <Radio
                    id="target-all-hosts"
                    checked={target === "all" && selectedTags?.length === 0}
                    onChange={() => {
                      setTarget("all");
                      setSelectedTags([]);
                    }}
                  />
                  <label htmlFor="target-all-hosts">All hosts</label>
                  <span className="text-secondary-foreground">{`(${endpoints.length})`}</span>
                </div>
                <div className="flex items-center gap-2 leading-5">
                  <Radio
                    id="target-tags"
                    checked={target === "tags"}
                    onChange={() => setTarget("tags")}
                  />
                  <label htmlFor="target-tags">By tag</label>
                  <span className="text-secondary-foreground">{`(${getSelectedTagCount()}) ${pluralize(
                    getSelectedTagCount(),
                    "host",
                  )} selected`}</span>
                </div>
              </>
            </section>
            {showTags && (
              <section className="flex flex-col gap-2">
                <h5 className="m-0 mb-1 border-stroke-line border-b pb-2 font-normal text-secondary-foreground text-sm leading-5">
                  Tags
                </h5>
                {endpointTags.length > 0 ? (
                  endpointTags.map((tag) => (
                    <div
                      key={tag}
                      className="flex items-center gap-2 break-all leading-5"
                    >
                      <Checkbox
                        id={tag}
                        value={tag}
                        checked={selectedTags?.includes(tag)}
                        onCheckedChange={() => handleSelectedTags(tag)}
                      />
                      <label htmlFor={tag}>{tag}</label>
                      <span className="text-secondary-foreground">
                        {`(${
                          endpoints.filter((e) => e.tags.includes(tag)).length
                        }) hosts`}
                      </span>
                    </div>
                  ))
                ) : (
                  <div>No tags available</div>
                )}
              </section>
            )}

            <Button
              intent="primary"
              disabled={upsertSchedules.isPending}
              onClick={(ev) => {
                ev.preventDefault();
                upsert();
              }}
              loading={upsertSchedules.isPending}
            >
              Save
            </Button>

            <section className="flex flex-col gap-2">
              <h5 className="m-0 mb-1 border-stroke-line border-b pb-2 font-normal text-secondary-foreground text-sm leading-5">
                Relevancy
              </h5>
              <p className="m-0 text-primary-foreground">
                Verified Security Tests will only run on relevant operating
                systems. Prelude Detect automatically determines which hosts are
                appropriate for running each test.
              </p>
            </section>
          </div>
        </div>
      </form>
    </DefaultOverlay>
  );
};

const RunCodeBlurb: React.FC<{ runCode: RunCode | undefined }> = ({
  runCode,
}) => {
  if (runCode === RunCodes.RUN_ONCE) {
    return (
      <p className="pt-2 text-secondary-foreground">
        Verified Security Tests will run only once at the next probe check in.
      </p>
    );
  }
  if (runCode === RunCodes.WEEKLY) {
    return (
      <p className="pt-2 text-secondary-foreground">
        Verified Security Tests will run weekly on a day chosen at random.
      </p>
    );
  }
  if (runCode === RunCodes.MONTHLY) {
    return (
      <p className="pt-2 text-secondary-foreground">
        Verified Security Tests will run monthly on a day chosen at random.
      </p>
    );
  }
  return null;
};

export default ScheduleOverlay;
